// 手机端使用单列输入与可换行工具栏，长地址完整阅读；电脑保留原布局，避免窄屏遮住表单或操作。
// 手机将明细按记录展示，保留原行事件；有交互表头或复杂行时保留可滑动表格，避免丢失排序、勾选和操作。
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { Loader2, Star } from "lucide-react";

const BAR_CLS = ["bg-red-400", "bg-orange-400", "bg-amber-400", "bg-lime-500", "bg-emerald-500"];

export default function SatisfactionBoard() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/engine/satisfaction/board").then(({ data }) => setData(data)).catch(() => setData({ rows: [], stats: {} }));
  }, [api]);
  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  const st = data.stats || {};
  const maxDist = Math.max(1, ...Object.values(st.dist || {}));
  const stars = (n) => "★".repeat(n) + "☆".repeat(5 - n);
  return (
    <div className="max-w-5xl" data-testid="satisfaction-board-page">
      <div className="mb-4">
        <h1 className="text-xl font-extrabold text-slate-800 flex items-center gap-2"><Star className="h-5 w-5 text-amber-500" />{T("业主满意度看板", "Owner Satisfaction Board")}</h1>
        <p className="text-xs text-slate-400 mt-0.5">{T("交付 30 天回访评分自动汇总 · 业主门户星级 + 留言直达管理层", "30-day post-delivery ratings, auto-collected from the owner portal")}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sat-stat-avg">
          <p className="text-xs text-slate-500">{T("平均评分", "Average score")}</p>
          <p className="text-2xl font-extrabold text-amber-500 mt-1">{st.avg != null ? st.avg : "—"}<span className="text-sm text-slate-300"> /5</span></p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sat-stat-resp">
          <p className="text-xs text-slate-500">{T("已收评价", "Responses")}</p>
          <p className="text-2xl font-extrabold text-slate-800 mt-1">{st.responses || 0}<span className="text-sm text-slate-300"> / {st.delivered || 0}</span></p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sat-stat-followups">
          <p className="text-xs text-slate-500">{T("回访短信已生成", "Follow-ups drafted")}</p>
          <p className="text-2xl font-extrabold text-slate-800 mt-1">{st.followupsSent || 0}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sat-stat-dist">
          <p className="text-xs text-slate-500 mb-1.5">{T("评分分布", "Distribution")}</p>
          {[5, 4, 3, 2, 1].map((n) => (
            <div key={n} className="flex items-center gap-1.5 text-[10px] text-slate-400">
              <span className="w-3">{n}</span>
              <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${BAR_CLS[n - 1]}`} style={{ width: `${((st.dist || {})[String(n)] || 0) / maxDist * 100}%` }} />
              </div>
              <span className="w-4 text-right">{(st.dist || {})[String(n)] || 0}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <ResponsiveTable className="w-full text-sm">
          <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
            <th className="px-3 py-2">{T("项目", "Project")}</th><th className="px-3 py-2">{T("业主", "Owner")}</th>
            <th className="px-3 py-2">{T("交付日", "Delivered")}</th><th className="px-3 py-2">{T("回访", "Follow-up")}</th>
            <th className="px-3 py-2">{T("评分", "Rating")}</th><th className="px-3 py-2">{T("留言", "Comment")}</th>
          </tr></thead>
          <tbody>
            {(data.rows || []).length === 0 ? <tr><td colSpan={6} className="text-center py-10 text-slate-300">{T("暂无已交付项目 — 项目完成 Closeout 后自动进入看板", "No delivered projects yet — closeout completion adds them here")}</td></tr> :
              data.rows.map((r) => (
                <tr key={r.projectId} className="border-b border-slate-50" data-testid={`sat-row-${r.projectId}`}>
                  <td className="px-3 py-2"><p className="font-semibold text-slate-700 text-xs">{r.address}</p><p className="text-[10px] text-slate-400">{r.projectId}{r.manager ? ` · PM ${r.manager}` : ""}</p></td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.clientName || "-"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.completedAt || "-"}</td>
                  <td className="px-3 py-2 text-xs">{r.followupAt ? <span className="text-emerald-600">✓ {r.followupAt}</span> : <span className="text-slate-300">{T("未到期", "Not due")}</span>}</td>
                  <td className="px-3 py-2">{r.score ? <span className="text-amber-500 font-bold text-xs" data-testid={`sat-score-${r.projectId}`}>{stars(r.score)} {r.score}/5</span> : <span className="text-[10px] text-slate-300">{T("待评价", "Pending")}</span>}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[240px]"><p className="break-words sm:line-clamp-2">{r.comment || "-"}</p>{r.submittedAt && <p className="text-[9px] text-slate-300">{r.submittedAt}</p>}</td>
                </tr>
              ))}
          </tbody>
        </ResponsiveTable>
      </div>
    </div>
  );
}
